import React from "react";
import { motion } from "framer-motion";

const TIERS = [
  { name: "Platinum", min: 90, color: "#60A5FA", gradient: ["#93C5FD", "#2563EB"] },
  { name: "Gold",     min: 80, color: "#D97706", gradient: ["#FCD34D", "#B45309"] },
  { name: "Silver",  min: 70, color: "#94A3B8", gradient: ["#CBD5E1", "#64748B"] },
  { name: "Bronze",  min: 60, color: "#C2410C", gradient: ["#FB923C", "#9A3412"] },
  { name: "Unranked",min: 0,  color: "#78716C", gradient: ["#A8A29E", "#57534E"] },
];

function getTier(score) {
  return TIERS.find((t) => score >= t.min) ?? TIERS[TIERS.length - 1];
}

// sm: small colored pill "Gold · 84"
function SmBadge({ tier, score }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono font-semibold text-white"
      style={{ background: tier.color }}
    >
      {tier.name} · {score}
    </span>
  );
}

// md: hexagon with tier name
// lg: hexagon with tier name + score + "Wishire Score"
function HexBadge({ tier, score, size }) {
  const dim = size === "lg" ? 120 : 80;
  const gradId = `tier-grad-${tier.name}-${size}`;

  return (
    <motion.div
      className="relative inline-flex items-center justify-center select-none"
      style={{ width: dim, height: dim * 1.1547 }}
      whileHover="shine"
    >
      {/* SVG hexagon */}
      <svg
        viewBox="0 0 100 115.47"
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={tier.gradient[0]} />
            <stop offset="100%" stopColor={tier.gradient[1]} />
          </linearGradient>
          {/* shine gradient */}
          <linearGradient id={`${gradId}-shine`} x1="0%" y1="0%" x2="60%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="40%" stopColor="rgba(255,255,255,0.35)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          <clipPath id={`${gradId}-clip`}>
            <polygon points="50,0 100,28.87 100,86.60 50,115.47 0,86.60 0,28.87" />
          </clipPath>
        </defs>

        {/* Fill */}
        <polygon
          points="50,0 100,28.87 100,86.60 50,115.47 0,86.60 0,28.87"
          fill={`url(#${gradId})`}
        />

        {/* Shine overlay */}
        <motion.polygon
          points="50,0 100,28.87 100,86.60 50,115.47 0,86.60 0,28.87"
          fill={`url(#${gradId}-shine)`}
          clipPath={`url(#${gradId}-clip)`}
          variants={{
            shine: {
              x: ["-100%", "150%"],
              transition: { duration: 0.6, ease: "easeInOut" },
            },
          }}
          initial={{ x: "-100%" }}
        />

        {/* Border */}
        <polygon
          points="50,2 98,29.87 98,85.60 50,113.47 2,85.60 2,29.87"
          fill="none"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="2"
        />
      </svg>

      {/* Text content */}
      <div className="relative z-10 flex flex-col items-center justify-center text-white leading-tight">
        {size === "lg" ? (
          <>
            <span className="font-display font-bold" style={{ fontSize: dim * 0.165 }}>
              {tier.name}
            </span>
            <span className="font-mono font-bold" style={{ fontSize: dim * 0.22 }}>
              {score}
            </span>
            <span
              className="font-mono uppercase tracking-widest opacity-70"
              style={{ fontSize: dim * 0.09 }}
            >
              Wishire
            </span>
          </>
        ) : (
          <span className="font-display font-bold" style={{ fontSize: dim * 0.185 }}>
            {tier.name}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default function ScoreTierBadge({ score = 0, size = "md", showLabel = false }) {
  const tier = getTier(score);

  if (size === "sm") {
    return <SmBadge tier={tier} score={score} />;
  }

  return (
    <div className="inline-flex flex-col items-center gap-2">
      <HexBadge tier={tier} score={score} size={size} />
      {showLabel && (
        <span className="text-xs font-mono text-stone-500">{tier.name} Tier</span>
      )}
    </div>
  );
}
